import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { X, Share as ShareIcon, Camera, Sticker, LayoutTemplate } from 'lucide-react';
import RapSheetCard from './RapSheetCard';
import { shareImage } from '../utils/shareImage';

// Full-screen viewer for a paired Rap Sheet: preview, render to PNG, share.
// Toggle between the full card and the lean story sticker.
const RapSheet = ({ sheet, onClose }) => {
  const [variant, setVariant] = useState('card');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const cardRef = useRef(null);

  // Scale the on-screen preview so the whole card fits between the toggle and the
  // pinned footer button — no scrolling. Capture is unaffected: html-to-image
  // grabs the inner card at its native 340px size (transform is layout-neutral).
  const previewAreaRef = useRef(null);
  const cardWrapRef = useRef(null);
  const [fitScale, setFitScale] = useState(1);
  useLayoutEffect(() => {
    const area = previewAreaRef.current, card = cardWrapRef.current;
    if (!area || !card) return;
    const measure = () => {
      const h = card.offsetHeight, w = card.offsetWidth; // layout size, transform-independent
      if (!h || !w) return;
      const s = Math.min(1, (area.clientHeight - 8) / h, (area.clientWidth - 8) / w);
      setFitScale(s > 0 ? s : 1);
    };
    measure();
    const t = setTimeout(measure, 400); // re-measure after fonts/logo settle
    window.addEventListener('resize', measure);
    return () => { clearTimeout(t); window.removeEventListener('resize', measure); };
  }, [variant, sheet]);

  useEffect(() => {
    const isiOSApp = window.navigator && window.navigator.standalone;
    const isMedia = window.matchMedia('(display-mode: standalone)').matches;
    const isNativeApp = window.Capacitor?.isNativePlatform?.();
    setIsStandalone(!!(isiOSApp || isMedia || isNativeApp));
  }, []);

  useEffect(() => { setPreviewUrl(null); }, [variant]);

  const handleGenerate = async () => {
    if (!cardRef.current) return;
    setIsGenerating(true);
    await new Promise(r => setTimeout(r, 100));
    const url = await cardRef.current.generateImage();
    setIsGenerating(false);
    if (url) { setPreviewUrl(url); handleShare(url); }
  };

  const handleShare = async (url) => {
    const target = url || previewUrl;
    if (!target || isSharing) return;
    setIsSharing(true);
    try { await shareImage(target, `benderquest-rap-sheet-${variant}.png`, 'My Bender Quest Rap Sheet'); }
    finally { setIsSharing(false); }
  };

  if (!sheet) return null;

  return (
    <div className="absolute inset-0 z-[3000] bg-black flex flex-col animate-in slide-in-from-bottom duration-300">
      <div className={`flex justify-between items-center border-b border-white/10 bg-zinc-900 ${isStandalone ? 'pt-16 px-4 pb-4' : 'p-4'}`}>
        <h2 className="text-lg font-black italic text-white uppercase tracking-tighter">Rap Sheet</h2>
        <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={24} /></button>
      </div>

      {/* Variant toggle */}
      <div className="flex gap-2 p-3 justify-center border-b border-white/5">
        <button onClick={() => setVariant('card')}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest border ${variant === 'card' ? 'bg-white text-black border-white' : 'text-zinc-400 border-zinc-700'}`}>
          <LayoutTemplate size={12} /> Full card
        </button>
        <button onClick={() => setVariant('sticker')}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest border ${variant === 'sticker' ? 'bg-white text-black border-white' : 'text-zinc-400 border-zinc-700'}`}>
          <Sticker size={12} /> Story sticker
        </button>
      </div>

      {/* Preview — fills remaining space; the card is scaled to fit (no scroll).
          Backdrop gradient is preview-only; the capture grabs the inner card. */}
      <div ref={previewAreaRef} className="flex-1 min-h-0 overflow-hidden flex items-center justify-center p-3">
        <div ref={cardWrapRef}
          style={{ transform: `scale(${fitScale})`, transformOrigin: 'center' }}
          className={`rounded-2xl p-3 bg-gradient-to-br from-pink-900/40 via-zinc-900 to-teal-900/30 ${variant === 'sticker' ? 'w-[344px]' : 'w-[380px]'}`}>
          <RapSheetCard ref={cardRef} sheet={sheet} variant={variant} />
        </div>
      </div>

      {/* Fixed footer — the action + tag are always visible, never scrolled off. */}
      <div className="shrink-0 border-t border-white/5 px-4 pt-3 pb-6 space-y-3">
        {!previewUrl ? (
          <button onClick={handleGenerate} disabled={isGenerating}
            className="w-full max-w-[400px] mx-auto bg-white hover:bg-zinc-200 text-black font-black py-4 rounded-xl uppercase tracking-widest active:scale-95 transition-all flex items-center justify-center gap-2 shadow-lg">
            {isGenerating ? 'Rendering…' : <><Camera size={18} /> Create & Share</>}
          </button>
        ) : (
          <button onClick={() => handleShare()} disabled={isSharing}
            className="w-full max-w-[400px] mx-auto bg-white hover:bg-zinc-200 text-black font-black py-4 rounded-xl uppercase tracking-widest active:scale-95 transition-all flex items-center justify-center gap-2 shadow-lg disabled:opacity-60">
            <ShareIcon size={18} /> {isSharing ? 'Sharing…' : 'Share / Save'}
          </button>
        )}
        <p className="text-center text-[9px] font-bold text-zinc-600 uppercase tracking-widest">
          {variant === 'sticker' ? 'Transparent PNG — drop it on your story photo' : 'Transparent PNG — post it or overlay it'}
        </p>
      </div>
    </div>
  );
};

export default RapSheet;
